<template>
  <div>
    <mds-section border="tertiary" bold :size="8" :title="translate('advisoryFee')">
      <template v-slot:mds-section-title-description>
        <InfoIconField
          :class="$style['advisory-fee-tooltip']"
          :show-info-icons="settings.showInfoIcons"
          show-learn-more
          :abbr-content="tryTranslate('advisoryFeeTooltip')"
          :detailed-content="tryTranslate('advisoryFeeDetailedMsg')"
          :labels="translatedLabels()"
        >
        </InfoIconField>
      </template>
      <template v-slot:mds-section-actions>
        <mds-switch
          v-model="includeAdvisoryFee"
          :disabled="settings.isActualWorkflow"
          hidden-label
          @change="emitData"
        >
          {{ translate('include') }}
        </mds-switch>
      </template>
      <template v-slot:default>
        <mds-form v-if="includeAdvisoryFee" size="small">
          <mds-fieldset>
            <template v-if="showAnnualAccountFee">
              <InfoIconField
                :show-info-icons="settings.showInfoIcons"
                :show-learn-more="false"
                :abbr-content="tryTranslate('annualAccountFeeTooltip')"
              >
                <mds-input
                  :label="`${translate('annualAccountFee')} ${currencySymbol}`"
                  size="small"
                  :value="formatter.number(annualAccountFeeAmount)"
                  @change="handleAnnualAccountFeeAmountChanged"
                >
                </mds-input>
              </InfoIconField>
            </template>
            <mds-section
              v-if="showAnnualAssetBaseFee"
              border="none"
              bold
              :size="8"
              :title="translate('annualAssetBased')"
              :class="$style['asset-based-fee-section']"
            >
              <mds-fieldset
                v-if="showFeeTypes"
                size="small"
                variation="radio-group"
                :class="$style['fee-type-radio-buttons']"
              >
                <mds-radio-button
                  v-for="(item, index) in annualFeeTypeTranslatedOptions"
                  :key="index"
                  v-model="annualFeeType"
                  size="small"
                  name="annualFeeType"
                  e2e="anual-asset-fee"
                  :value="item.value"
                  @input="handleAnnualFeeTypeChanged"
                >
                  {{ item.text }}
                </mds-radio-button>
              </mds-fieldset>
              <fee-choice
                v-if="showFeeChoice"
                :mwc-id="getMwcIdForChild('fee-choice-portfolio')"
                :environment="environment"
                :user-info="userInfo"
                :asset-based-fee-config="assetBasedFeeConfig"
                :is-various-fee-schedule-id="advisoryFeeSetting.isVariousFeeScheduleId"
                :advisory-fee-schedule="advisoryFeeSchedule"
                :fee-schedule-id="feeScheduleId"
                :all-fee-schedule-list="allFeeScheduleList"
                @update-fee-schedule-id="handleFeeScheduleIdChange"
                @before-close="$emit('before-close', $event)"
                @fee-schedules-updated="$emit('fee-schedules-updated', $event)"
              />
              <template v-if="showAnnualAssetBaseFeeInput">
                <varies-field
                  v-if="isFlat"
                  :class="$style['secondary-label']"
                  :is-varies="advisoryFeeSetting && advisoryFeeSetting.isVariousFlatAnnualFeeAmount"
                  :labels="getVariesFieldLabels()"
                  :value="annualFeeAmount"
                  :default-value="defaultVals.annualFeeAmount"
                  :allow-leave-blank="false"
                  @update-field="handleAnnualFeeAmountChanged"
                >
                </varies-field>
                <varies-field
                  v-else-if="isMaxFee"
                  :class="$style['secondary-label']"
                  :is-varies="isVariousAnnualMaxFee"
                  :labels="getVariesFieldLabels()"
                  :value="annualMaxFee"
                  :default-value="advisoryFeeSchedule.maxFee"
                  :allow-leave-blank="false"
                  :range="maxFeeRange"
                  @input-invalid="triggerAlert('annualFeeAmount')"
                  @update-field="handleAnnualMaxFeeChanged"
                >
                </varies-field>
                <template v-else-if="isHistoricMaxFee">
                  <varies-field
                    id="historic-max-fee-input"
                    :class="$style['secondary-label']"
                    :is-varies="isVariousAnnualMaxFee"
                    :labels="getVariesFieldLabels()"
                    :value="annualMaxFee"
                    :range="historicMaxFeeRange"
                    :placeholder="translate('enterToChange')"
                    @input-invalid="triggerAlert('annualFeeAmount')"
                    @update-field="handleAnnualMaxFeeChanged"
                    @focus="showHistoricMaxFeeInputPopover = true"
                    @blur="showHistoricMaxFeeInputPopover = false"
                  >
                  </varies-field>
                  <mds-popover
                    v-model="showHistoricMaxFeeInputPopover"
                    triggered-by="historic-max-fee-input"
                    :position="['bottom-right']"
                  >
                    {{ translate('historicMaxFeeInputPopoverMsg') }}
                  </mds-popover>
                </template>
              </template>
              <varies-field
                v-if="showDiscountInput"
                :class="$style['secondary-label']"
                :is-varies="isVariousDiscount"
                :labels="getVariesFieldLabels(true)"
                :value="discount"
                :default-value="defaultVals.discount"
                :range="getDiscountRange()"
                :allow-leave-blank="false"
                @input-invalid="triggerAlert('discount')"
                @update-field="handleDiscountChanged"
              >
              </varies-field>
              <template v-if="showAnnualFeeFrequency">
                <mds-select
                  v-model.number="annualFeeChargeFrequency"
                  size="small"
                  :class="$style['secondary-label']"
                  :label="translate('billedFrequency')"
                  :placeholder="advisoryFeeSetting.isVariousFeeFrequency ? variesLabel : undefined"
                  e2e="annual-fee-charge-frequency"
                  :options="annualFeeChargeFrequencyOptions"
                  @change="emitData"
                >
                </mds-select>
              </template>
            </mds-section>
            <mds-fieldset
              v-if="showPayFeeTime"
              size="small"
              variation="radio-group"
              :legend="translate('whenFeesBilled')"
              :class="$style['secondary-legend']"
            >
              <mds-radio-button
                v-for="(item, index) in annualFeePayOnBeginOptions"
                :key="index"
                v-model="annualFeePayOnBegin"
                name="annualFeePayOnBegin"
                e2e="annual-fee-pay-on-begin"
                size="small"
                :value="item.value"
                @input="emitData"
              >
                {{ item.text }}
              </mds-radio-button>
            </mds-fieldset>
            <mds-fieldset
              v-if="showPayFeeMethod"
              size="small"
              variation="radio-group"
              :legend="translate('billAssetBasedFee')"
              :class="$style['secondary-legend']"
            >
              <mds-radio-button
                v-for="(item, index) in annualFeePayMethodOptions"
                :key="index"
                v-model.number="annualFeePayMethod"
                name="annualFeePayMethod"
                e2e="annual-fee-pay-method"
                size="small"
                :value="item.value"
                @input="emitData"
              >
                {{ item.text }}
              </mds-radio-button>
            </mds-fieldset>
            <mds-fieldset
              v-if="annualFeePayMethod === 1 && showPayCashFirst"
              size="small"
              variation="radio-group"
              :legend="translate('useCashFirst')"
              :class="$style['secondary-legend']"
            >
              <mds-radio-button
                v-for="(item, index) in annualFeePayCashFirstOptions"
                :key="index"
                v-model="annualFeePayCashFirst"
                name="annaulFeePayCashFirst"
                e2e="annual-fee-pay-cash-first"
                size="small"
                :value="item.value"
                @input="emitData"
              >
                {{ item.text }}
              </mds-radio-button>
            </mds-fieldset>
          </mds-fieldset>
        </mds-form>
      </template>
    </mds-section>
  </div>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import MdsForm from '@mds/form';
import MdsFieldset from '@mds/fieldset';
import MdsSection from '@mds/section';
import MdsSwitch from '@mds/switch';
import MdsInput from '@mds/input';
import MdsSelect from '@mds/select';
import MdsRadioButton from '@mds/radio-button';
import MdsPopover from '@mds/popover';
import InfoIconField from 'hypo-shared-utils/src/components/InfoIconField';
import FeeChoice from '../shared/fee-choice/FeeChoice';
import VariesField from '../shared/VariesField';
import {
  parseNumber,
  isInvalidAmount,
  isPercentageInRange,
  isMaxFee,
  isHistoricMaxFee,
} from '../../utils/utils';
import {
  getMaxFeeRange,
  getHistoricMaxFeeRange,
  getDiscountRange,
  triggerAdvisoryFeeAlert,
  calcAnnualMaxFee,
  calcDiscount,
  getAnnualFeeTypeOptions,
  getUpdatedMaxFeeAndDiscountWhenFeeScheduleChange,
} from '../shared/shared-advisory-fee-helpers';
import {
  annualFeeChargeFrequencies,
  payFeeMethods,
  payOnBeginOptions,
  booleanOptions,
} from '../../utils/input-options';
import { FEE_CONFIG_TYPE, FEE_TYPES } from '../../utils/constants';

let uid = 0;
const { FLAT, VARIES } = FEE_TYPES;
const { EVERYTHING_ON, ONLY_FEE_CHOICE } = FEE_CONFIG_TYPE;

export default {
  name: 'advisory-fee',
  components: {
    MdsForm,
    MdsFieldset,
    MdsSection,
    MdsSwitch,
    MdsInput,
    MdsSelect,
    MdsRadioButton,
    MdsPopover,
    FeeChoice,
    VariesField,
    InfoIconField,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    currencySymbol: {
      type: String,
      default: '$',
    },
    showAnnualAccountFee: {
      type: Boolean,
      default: true,
    },
    showAnnualAssetBaseFee: {
      type: Boolean,
      default: true,
    },
    showAnnualFeeFrequency: {
      type: Boolean,
      default: true,
    },
    showPayFeeMethod: {
      type: Boolean,
      default: true,
    },
    showPayFeeTime: {
      type: Boolean,
      default: true,
    },
    showPayCashFirst: {
      type: Boolean,
      default: true,
    },
    advisoryFeeSetting: Object,
    assetBasedFeeConfig: {
      type: Object,
      default: () => {},
    },
    environment: String,
    userInfo: Object,
    allFeeScheduleList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const {
      includeAdvisoryFee,
      annualAccountFeeAmount = 0,
      annualFeeAmount,
      annualMaxFee,
      discount,
      annualFeeChargeFrequency,
      annualFeePayMethod,
      annualFeePayOnBegin,
      annualFeePayCashFirst,
      annualFeeType,
      feeScheduleId,
    } = this.advisoryFeeSetting;
    return {
      defaultConfig: {
        settings: {
          languageId: this.getComponentConfig().settings.languageId || 'en-US',
        },
        format: {
          longDecimal: {
            dataType: 'number',
            minimumFractionDigits: 0,
            maximumFractionDigits: 4,
          },
        },
      },
      includeAdvisoryFee,
      annualFeeAmount,
      annualMaxFee,
      discount,
      annualFeeChargeFrequency,
      annualFeePayMethod,
      annualFeeType,
      annualFeePayOnBegin,
      annualFeePayCashFirst,
      annualAccountFeeAmount,
      showFeeScheduleModal: false,
      feeScheduleId,
      showHistoricMaxFeeInputPopover: false,
    };
  },
  computed: {
    defaultVals() {
      return this.settings?.preference;
    },
    annualFeeChargeFrequencyOptions() {
      const frequencyOptions = this.translateComponentOptions(annualFeeChargeFrequencies);
      return frequencyOptions;
    },
    annualFeePayMethodOptions() {
      return this.translateComponentOptions(payFeeMethods);
    },
    annualFeePayOnBeginOptions() {
      return this.translateComponentOptions(payOnBeginOptions);
    },
    annualFeePayCashFirstOptions() {
      return this.translateComponentOptions(booleanOptions);
    },
    annualFeeTypeTranslatedOptions() {
      return this.translateComponentOptions(
        getAnnualFeeTypeOptions(
          this.assetBasedFeeConfig,
          this.advisoryFeeSetting.isVariousAnnualFeeType
        )
      );
    },
    showFeeTypes() {
      return this.assetBasedFeeConfig.assetBasedFeeConfigType === 3;
    },
    advisoryFeeSchedule() {
      return this.allFeeScheduleList.find(
        feeSchedule => feeSchedule.feeScheduleId === this.feeScheduleId
      );
    },
    isFlat() {
      return this.annualFeeType === FLAT;
    },
    isMaxFee() {
      return isMaxFee(this.advisoryFeeSchedule);
    },
    isHistoricMaxFee() {
      return isHistoricMaxFee(this.advisoryFeeSchedule);
    },
    maxFeeRange() {
      return getMaxFeeRange(this.advisoryFeeSchedule);
    },
    historicMaxFeeRange() {
      return getHistoricMaxFeeRange(this.advisoryFeeSchedule);
    },
    annualMaxFeeRange() {
      if (this.isMaxFee) {
        return this.maxFeeRange;
      }
      return this.historicMaxFeeRange;
    },
    isDiscountAllowedForMaxFee() {
      return (this.isMaxFee || this.isHistoricMaxFee) && this.advisoryFeeSchedule?.allowMaxDiscount;
    },
    showAnnualAssetBaseFeeInput() {
      return this.isFlat || this.isDiscountAllowedForMaxFee;
    },
    showDiscountInput() {
      return !this.isHistoricMaxFee && this.advisoryFeeSchedule?.allowMaxDiscount;
    },
    showFeeChoice() {
      return ![FLAT, VARIES].includes(this.annualFeeType);
    },
    isVariousAnnualMaxFee() {
      return this.isDiscountAllowedForMaxFee && this.advisoryFeeSetting?.isVariousAnnualMaxFee;
    },
    isVariousDiscount() {
      return (
        this.advisoryFeeSchedule?.allowMaxDiscount && this.advisoryFeeSetting?.isVariousDiscount
      );
    },
    variesLabel() {
      const labelKey = this.settings.isActualWorkflow
        ? 'variesAcrossInvestments'
        : 'variesAcrossHoldings';
      return this.translate(labelKey);
    },
    annualFeeFieldLabel() {
      return this.settings.enableThreeReturns
        ? this.translate('annualProposedFeePercent')
        : this.translate('annualFee');
    },
  },
  watch: {
    advisoryFeeSetting: {
      handler(newVal) {
        this.annualAccountFeeAmount = newVal.annualAccountFeeAmount;
        this.annualFeeAmount = newVal.annualFeeAmount;
        this.annualMaxFee = newVal.annualMaxFee;
        this.discount = newVal.discount;
        this.annualFeeChargeFrequency = newVal.annualFeeChargeFrequency;
        this.annualFeeType = newVal.annualFeeType;
        this.feeScheduleId = newVal.feeScheduleId;
      },
      deep: true,
    },
  },
  created() {
    uid += 1;
    this.uniqueId = uid;
  },
  methods: {
    handleFeeScheduleIdChange(id) {
      this.feeScheduleId = id;
      if (id !== this.defaultVals.feeScheduleId) {
        this.discount = 0;
        this.annualMaxFee = this.advisoryFeeSchedule.maxFee;
      }
      if (id !== this.advisoryFeeSetting.feeScheduleId) {
        const { annualMaxFee, discount } = getUpdatedMaxFeeAndDiscountWhenFeeScheduleChange(
          this.advisoryFeeSetting,
          this.advisoryFeeSchedule,
          this.defaultVals.discount
        );
        this.annualMaxFee = annualMaxFee;
        this.discount = discount;
      }
      this.emitData();
    },
    translateComponentOptions(options) {
      return options.map(({ labelKey, value }) => {
        return {
          text: this.translate(labelKey),
          value,
        };
      });
    },
    emitData() {
      this.$emit('advisory-fee-setting-changed', {
        ...this.advisoryFeeSetting,
        includeAdvisoryFee: this.includeAdvisoryFee,
        annualAccountFeeAmount: this.annualAccountFeeAmount,
        annualMaxFee: this.annualMaxFee,
        annualFeeAmount: this.annualFeeAmount,
        discount: this.discount,
        annualFeeChargeFrequency: this.annualFeeChargeFrequency,
        annualFeePayMethod: this.annualFeePayMethod,
        annualFeePayOnBegin: this.annualFeePayOnBegin,
        annualFeePayCashFirst: this.annualFeePayCashFirst,
        annualFeeType: this.annualFeeType,
        feeScheduleId: this.feeScheduleId,
      });
    },
    triggerAlert(fieldName) {
      triggerAdvisoryFeeAlert(this.advisoryFeeSchedule, fieldName, this);
    },
    handleAnnualFeeAmountChanged(annualFeeAmount) {
      this.annualFeeAmount = annualFeeAmount;
      this.emitData();
    },
    clearDiscountAndAnnualMaxFee() {
      this.annualMaxFee = undefined;
      this.discount = undefined;
      this.emitData();
    },
    handleAnnualMaxFeeChanged(annualMaxFee) {
      if (annualMaxFee === undefined) {
        this.clearDiscountAndAnnualMaxFee();
        return;
      }
      const isAnnualMaxFeeValid = isPercentageInRange(
        annualMaxFee,
        this.annualMaxFeeRange.min,
        this.annualMaxFeeRange.max
      );
      if (isAnnualMaxFeeValid) {
        this.annualMaxFee = annualMaxFee;
        this.discount = undefined;
        if (!isHistoricMaxFee(this.advisoryFeeSchedule)) {
          this.discount = calcDiscount(this.advisoryFeeSchedule?.maxFee, annualMaxFee);
        }
        this.emitData();
      }
    },
    handleDiscountChanged(discount) {
      if (discount === undefined) {
        this.clearDiscountAndAnnualMaxFee();
        return;
      }
      const isDiscountValid = isPercentageInRange(
        discount,
        0,
        this.advisoryFeeSchedule?.maxDiscount
      );
      if (isDiscountValid) {
        this.discount = discount;
        this.annualMaxFee = calcAnnualMaxFee(this.advisoryFeeSchedule?.maxFee, discount);
        this.emitData();
      }
    },
    handleAnnualAccountFeeAmountChanged(e) {
      const amount = parseNumber(e.target.value, 2);
      if (isInvalidAmount(amount) || amount === this.annualAccountFeeAmount) {
        e.target.value = this.formatter.number(this.annualAccountFeeAmount);
        return;
      }
      this.annualAccountFeeAmount = amount;
      this.emitData();
    },
    handleAnnualFeeTypeChanged() {
      this.annualFeeAmount = this.defaultVals.annualFeeAmount;
      this.feeScheduleId = this.defaultVals.feeScheduleId;
      this.annualMaxFee = undefined;
      this.discount = undefined;
      this.emitData();
    },
    getVariesFieldLabels(isDiscount = false) {
      // When ONLY_PERCENT, we will use mds section title to act as annualFee input title
      const annualFeeLabel = [ONLY_FEE_CHOICE, EVERYTHING_ON].includes(
        this.assetBasedFeeConfig.assetBasedFeeConfigType
      )
        ? this.annualFeeFieldLabel
        : null;
      const fieldLabel = isDiscount ? this.translate('Discount %') : annualFeeLabel;

      return {
        fieldLabel,
        varies: this.variesLabel,
        clear: this.translate('clear'),
      };
    },
    getDiscountRange() {
      return getDiscountRange(this.advisoryFeeSchedule);
    },
    tryTranslate(key) {
      if (this.labeller.exists(key)) {
        return this.translate(key);
      }
      return '';
    },
    translatedLabels() {
      return {
        learnMore: this.translate('learnMore'),
        close: this.translate('close'),
        modalTitle: this.translate('moreInformation'),
      };
    },
  },
};
</script>

<style lang="scss" module>
@import '~@mds/constants';
@import '@mds/icon/src/icon_mixins';
@import '~@mds/link';

.advisory-fee-tooltip {
  margin-top: $mds-space-half-x;
  margin-bottom: 0px;
}

.input-label-with-tooltip {
  @include mds-body-text-s();
  margin: 0;
  margin-bottom: $mds-space-half-x;
  display: flex;
  align-items: center;
  .mds-button {
    height: $mds-space-2-and-a-half-x;
    width: $mds-space-2-and-a-half-x;
    margin-left: $mds-space-half-x;
  }
}

.asset-based-fee-section {
  margin-bottom: $mds-space-2-x;
  .mds-section__header-container {
    margin-bottom: $mds-space-1-x;
  }
  .fee-type-radio-buttons {
    margin-bottom: $mds-space-1-and-a-half-x;
  }
}
.discount {
  margin-bottom: $mds-space-2-x;
}

.learn-more-link__wrapper {
  display: flex;
  .learn-more-link {
    margin-top: $mds-space-three-quarter-x;
    margin-left: auto;
    @include mds-link();
    color: $mds-text-color-interactive-primary;
  }
}

.secondary-label {
  .mds-label__text {
    color: $mds-text-color-secondary;
  }
}

.secondary-legend {
  .mds-fieldset__legend {
    color: $mds-text-color-secondary;
  }
}
</style>
